import React, { useState, useEffect, useRef, useCallback } from 'react';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';
import { X, Trophy, Play, Pause, Square, Upload, Mic, Trash2, Volume2, Loader2, Music, Eye, EyeOff, ArrowLeft, Wind, Sparkles, Timer, Brain, Focus, ChevronRight } from 'lucide-react';
import { Howl } from 'howler';
import { Slider } from './ui/slider';
import axios from 'axios';
import useAppSettings from '../hooks/useAppSettings';
import useSequencePlayer from './sportmental/useSequencePlayer';
import BreathingSportModule from './sportmental/BreathingSportModule';
import CoachVoiceAdmin from './sportmental/CoachVoiceAdmin';
import ScriptViewer from './sportmental/ScriptViewer';
import {
  SPORTS, SENSATIONS, EMOTIONS, CHAMPION_AFFIRMATIONS, BLOCAGE_SESSIONS,
  buildVisualisationScript, buildRoutineScript, buildInductionScript, INDUCTION_DURATIONS, getScriptForTarget
} from './sportmental/data';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  { id: 'visualisation', name: 'Visualisation de la performance', desc: 'Imaginez votre réussite dans les moindres détails', Icon: Focus, color: 'text-orange-300' },
  { id: 'respiration', name: 'Respirations Sport', desc: 'Anti-stress, activation, récupération', Icon: Wind, color: 'text-cyan-300' },
  { id: 'affirmations', name: 'Affirmations de Champion', desc: 'Renforcez votre mental avec des phrases puissantes', Icon: Sparkles, color: 'text-amber-300' },
  { id: 'routine', name: 'Routine d\'avant-match', desc: 'Enchaînement complet : respiration, visualisation, activation', Icon: Timer, color: 'text-emerald-300' },
  { id: 'blocages', name: 'Gestion des blocages', desc: 'Peur de l\'échec, retour de blessure, sommeil', Icon: Brain, color: 'text-purple-300' }
];

const chipClass = (active) => `px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
  active ? 'bg-orange-500/30 border border-orange-400/60 text-orange-300' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
}`;

const CustomizeBlock = ({ sport, setSport, sensation, setSensation, emotion, setEmotion }) => (
  <div className="p-4 bg-white/5 rounded-2xl space-y-4">
    <p className="text-white/60 text-xs uppercase tracking-wider">Personnalisez votre séance</p>
    <div>
      <p className="text-white/70 text-xs mb-2">Votre sport</p>
      <div className="flex flex-wrap gap-2" data-testid="sport-selector">
        {SPORTS.map(s => (
          <button key={s.id} onClick={() => setSport(s)} className={chipClass(sport.id === s.id)} data-testid={`sport-${s.id}`}>{s.name}</button>
        ))}
      </div>
    </div>
    <div>
      <p className="text-white/70 text-xs mb-2">Sensation recherchée</p>
      <div className="flex flex-wrap gap-2" data-testid="sensation-selector">
        {SENSATIONS.map(s => (
          <button key={s.id} onClick={() => setSensation(s)} className={chipClass(sensation.id === s.id)} data-testid={`sensation-${s.id}`}>{s.name}</button>
        ))}
      </div>
    </div>
    <div>
      <p className="text-white/70 text-xs mb-2">Émotion positive à ancrer</p>
      <div className="flex flex-wrap gap-2" data-testid="emotion-selector">
        {EMOTIONS.map(e => (
          <button key={e.id} onClick={() => setEmotion(e)} className={chipClass(emotion.id === e.id)} data-testid={`emotion-${e.id}`}>{e.name}</button>
        ))}
      </div>
    </div>
  </div>
);

const PlayButton = ({ player, onStart, startLabel = 'Lancer ma séance', startTestId = 'start-sport-session', stopTestId = 'stop-sport-session' }) => (
  <div className="flex flex-col items-center gap-3">
    {!player.isPlaying ? (
      <button onClick={onStart} disabled={player.isLoading}
        className="px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold text-lg hover:scale-105 transition-transform shadow-xl flex items-center gap-2 disabled:opacity-50"
        data-testid={startTestId}>
        {player.isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Play className="w-6 h-6" />}
        {player.isLoading ? 'Préparation...' : startLabel}
      </button>
    ) : (
      <div className="flex items-center gap-3">
        <button onClick={player.stop}
          className="px-8 py-4 rounded-full bg-white/15 backdrop-blur-sm text-white font-semibold text-lg hover:bg-white/25 transition-all flex items-center gap-2"
          data-testid={stopTestId}>
          <Square className="w-5 h-5" /> Arrêter
        </button>
        <span className="text-white/60 text-sm" data-testid="sport-session-progress">Étape {player.progress.current}/{player.progress.total}</span>
      </div>
    )}
  </div>
);

const SportMentalPanel = ({ isOpen, onClose, isAdmin = false }) => {
  const { settings, update } = useAppSettings();
  const [activeModule, setActiveModule] = useState(null);
  const [sport, setSport] = useState(SPORTS[0]);
  const [sensation, setSensation] = useState(SENSATIONS[0]);
  const [emotion, setEmotion] = useState(EMOTIONS[0]);
  const [voiceGender, setVoiceGender] = useState(() => localStorage.getItem('sportmental_voice') || 'female');
  const [inductionEnabled, setInductionEnabled] = useState(false);
  const [inductionMin, setInductionMin] = useState(5);
  const [recordingTarget, setRecordingTarget] = useState(null);
  const [ambientSounds, setAmbientSounds] = useState([]);
  const [activeAmbients, setActiveAmbients] = useState({});
  const [ambientVolumes, setAmbientVolumes] = useState({});
  const [coachAudios, setCoachAudios] = useState([]);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [scriptView, setScriptView] = useState(null);

  const player = useSequencePlayer();
  const ambientRefs = useRef({});
  const audioHowlRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveModule(null);
    axios.get(`${API}/api/ambient-sounds`).then(r => setAmbientSounds(r.data)).catch(() => {});
    axios.get(`${API}/api/sport-mental/audios`).then(r => setCoachAudios(r.data)).catch(() => {});
  }, [isOpen]);

  const stopAllAudio = useCallback(() => {
    player.stop();
    if (audioHowlRef.current) { audioHowlRef.current.stop(); audioHowlRef.current.unload(); audioHowlRef.current = null; }
    setPlayingAudioId(null);
    Object.values(ambientRefs.current).forEach(h => { if (h) { h.stop(); h.unload(); } });
    ambientRefs.current = {};
    setActiveAmbients({});
  }, [player]);

  const handleClose = () => { stopAllAudio(); onClose(); };
  const backToHub = () => { player.stop(); setActiveModule(null); };

  const setVoice = (g) => { setVoiceGender(g); localStorage.setItem('sportmental_voice', g); };

  const toggleAmbient = (sound) => {
    if (activeAmbients[sound.id]) {
      if (ambientRefs.current[sound.id]) { ambientRefs.current[sound.id].stop(); ambientRefs.current[sound.id].unload(); delete ambientRefs.current[sound.id]; }
      setActiveAmbients(prev => { const n = { ...prev }; delete n[sound.id]; return n; });
    } else {
      const url = sound.audio_url.startsWith('http') ? sound.audio_url : `${API}${sound.audio_url}`;
      const volume = ambientVolumes[sound.id] || 0.3;
      ambientRefs.current[sound.id] = new Howl({ src: [url], html5: true, loop: true, volume });
      ambientRefs.current[sound.id].play();
      setActiveAmbients(prev => ({ ...prev, [sound.id]: true }));
      if (!ambientVolumes[sound.id]) setAmbientVolumes(prev => ({ ...prev, [sound.id]: 0.3 }));
    }
  };

  const updateAmbientVolume = (soundId, volume) => {
    setAmbientVolumes(prev => ({ ...prev, [soundId]: volume }));
    if (ambientRefs.current[soundId]) ambientRefs.current[soundId].volume(volume);
  };

  const toggleCoachAudio = (audio) => {
    if (playingAudioId === audio.id) {
      if (audioHowlRef.current) { audioHowlRef.current.stop(); audioHowlRef.current.unload(); audioHowlRef.current = null; }
      setPlayingAudioId(null);
      return;
    }
    if (audioHowlRef.current) { audioHowlRef.current.stop(); audioHowlRef.current.unload(); }
    audioHowlRef.current = new Howl({ src: [`${API}${audio.audio_url}`], html5: true, onend: () => setPlayingAudioId(null) });
    audioHowlRef.current.play();
    setPlayingAudioId(audio.id);
  };

  const uploadAudio = async (file, category, name, target = null) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name || file.name.replace(/\.[^/.]+$/, ''));
    formData.append('category', category);
    if (target) formData.append('target', target);
    try {
      const res = await axios.post(`${API}/api/sport-mental/audios/upload`, formData);
      setCoachAudios(prev => [...prev.filter(a => !(target && a.category === 'voice' && a.target === target)), res.data]);
    } catch (e) {
      console.error('Upload error:', e);
      alert('Erreur lors de l\'upload');
    } finally { setUploading(false); }
  };

  const pickFile = (category, target = null, label = null) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.mp3,.wav,.ogg,.m4a,.webm';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (target) {
          uploadAudio(file, category, label || target, target);
        } else {
          const name = prompt('Nom de cet audio :', file.name.replace(/\.[^/.]+$/, ''));
          if (name !== null) uploadAudio(file, category, name);
        }
      }
    };
    input.click();
  };

  const startRecording = async (target = null, label = null) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(recordChunksRef.current, { type: 'audio/webm' });
        if (target) {
          const file = new File([blob], `voice_${target}_${Date.now()}.webm`, { type: 'audio/webm' });
          uploadAudio(file, 'voice', label || target, target);
        } else {
          const name = prompt('Nom de votre séance enregistrée :', 'Ma séance coach');
          if (name !== null) {
            const file = new File([blob], `seance_${Date.now()}.webm`, { type: 'audio/webm' });
            uploadAudio(file, 'seance', name);
          }
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(!target);
      setRecordingTarget(target);
    } catch (e) {
      console.error('Micro error:', e);
      alert('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    setIsRecording(false);
    setRecordingTarget(null);
  };

  const deleteAudio = async (audioId) => {
    try {
      await axios.delete(`${API}/api/sport-mental/audios/${audioId}`);
      setCoachAudios(prev => prev.filter(a => a.id !== audioId));
      if (playingAudioId === audioId && audioHowlRef.current) {
        audioHowlRef.current.stop(); audioHowlRef.current.unload(); setPlayingAudioId(null);
      }
    } catch (e) { console.error('Delete error:', e); }
  };

  const [keepAwake, toggleKeepAwake] = useKeepAwake('sportmental', isOpen);

  useEffect(() => {
    // If the user's saved voice was hidden by the admin, fall back to a visible one
    if (isAdmin || !isOpen) return;
    const vis = settings.sport_mental_voices_visible || {};
    const ok = (id) => vis[id] !== false;
    if (!ok(voiceGender)) {
      const first = ['female', 'male'].find(ok);
      if (first) { setVoiceGender(first); localStorage.setItem('sportmental_voice', first); }
    }
  }, [isOpen, settings, isAdmin, voiceGender]);

  if (!isOpen) return null;

  const seances = coachAudios.filter(a => a.category === 'seance');
  const suggestions = coachAudios.filter(a => a.category === 'suggestion');
  const coachVoiceMap = Object.fromEntries(coachAudios.filter(a => a.category === 'voice' && a.target).map(a => [a.target, a]));
  const hasCoachVoices = Object.keys(coachVoiceMap).length > 0;
  const currentModule = MODULES.find(m => m.id === activeModule);

  const modVis = settings.sport_mental_modules_visible || {};
  const voiceVis = settings.sport_mental_voices_visible || {};
  const isModuleVisible = (id) => modVis[id] !== false;
  const isVoiceVisible = (id) => voiceVis[id] !== false;
  const visibleModules = MODULES.filter(m => isAdmin || isModuleVisible(m.id));

  const openScript = (target, label) => {
    const s = getScriptForTarget(target, { sport, sensation, emotion });
    setScriptView({ target, title: s.title || label, lines: s.lines });
  };

  const startGuidedSession = (script, target) => {
    if (voiceGender === 'coach') {
      const urls = [];
      if (inductionEnabled) {
        const ind = coachVoiceMap[`induction_${inductionMin}`];
        if (ind) urls.push(ind.audio_url);
      }
      const main = coachVoiceMap[target];
      if (!main) {
        alert('Aucun enregistrement de votre voix pour cette séance. Utilisez les boutons 🎙️ Ma voix (mode admin) pour l\'enregistrer.');
        return;
      }
      urls.push(main.audio_url);
      player.playUrls(urls, target);
    } else {
      const segments = inductionEnabled ? [...buildInductionScript(inductionMin), ...script] : script;
      player.play(segments, voiceGender, target);
    }
  };

  const coachAdminProps = {
    coachVoiceMap, isAdmin, uploading, recordingTarget,
    onPickFile: (target, label) => pickFile('voice', target, label),
    onStartRecord: (target, label) => { startRecording(target, label); openScript(target, label); },
    onStopRecord: stopRecording,
    onDelete: deleteAudio,
    onShowScript: openScript
  };

  const InductionBlock = () => (
    <div className="p-4 bg-white/5 rounded-2xl space-y-3 border border-indigo-500/20" data-testid="induction-block">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white text-sm font-medium">Induction hypnotique</p>
          <p className="text-white/50 text-xs">Entrez d'abord en état d'hypnose légère pour amplifier la séance</p>
        </div>
        <button
          onClick={() => setInductionEnabled(v => !v)}
          className={`relative w-12 h-6 rounded-full transition-colors ${inductionEnabled ? 'bg-indigo-500' : 'bg-white/15'}`}
          data-testid="induction-toggle">
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${inductionEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>
      {inductionEnabled && (
        <div className="flex gap-2" data-testid="induction-durations">
          {INDUCTION_DURATIONS.map(m => (
            <button key={m} onClick={() => setInductionMin(m)}
              className={`px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                inductionMin === m ? 'bg-indigo-500/30 border border-indigo-400/60 text-indigo-300' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
              }`}
              data-testid={`induction-${m}min`}>
              {m} min
            </button>
          ))}
        </div>
      )}
      {inductionEnabled && isAdmin && (
        <CoachVoiceAdmin target={`induction_${inductionMin}`} label={`Induction hypnotique ${inductionMin} min`} {...coachAdminProps} />
      )}
    </div>
  );

  const VoiceSelector = () => {
    const showFemale = isAdmin || isVoiceVisible('female');
    const showMale = isAdmin || isVoiceVisible('male');
    const showCoach = (isAdmin || (isVoiceVisible('coach') && hasCoachVoices));
    return (
      <div className="p-4 bg-white/5 rounded-2xl">
        <p className="text-white/70 text-xs mb-2">Voix</p>
        <div className="flex flex-wrap gap-2">
          {showFemale && <button onClick={() => setVoice('female')} className={chipClass(voiceGender === 'female')} data-testid="sport-voice-female">Denise · Féminine</button>}
          {showMale && <button onClick={() => setVoice('male')} className={chipClass(voiceGender === 'male')} data-testid="sport-voice-male">Henri · Masculine</button>}
          {showCoach && <button onClick={() => setVoice('coach')} className={chipClass(voiceGender === 'coach')} data-testid="sport-voice-coach">Coach · Ma voix</button>}
        </div>
        {voiceGender === 'coach' && (
          <p className="text-white/40 text-[11px] mt-2">
            La séance sera lue avec les enregistrements de la voix du coach{isAdmin ? ' — enregistrez chaque script avec les boutons 🎙️ Ma voix ci-dessous.' : '.'}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 overflow-y-auto" data-testid="sport-mental-panel">
      <div className="min-h-full bg-gradient-to-b from-slate-950 via-orange-950/20 to-slate-950">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur-md px-4 pt-10 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeModule ? (
              <button onClick={backToHub} className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all" data-testid="back-to-hub">
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <Trophy className="w-6 h-6 text-orange-400" />
            )}
            <h2 className="text-white text-lg sm:text-xl font-heading font-semibold">
              {currentModule ? currentModule.name : 'Préparation Mentale'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-sportmental"
              className="px-3 py-2 rounded-full text-xs font-medium bg-white/10 text-white/80 hover:bg-white/20" />
            <button onClick={handleClose} className="p-2.5 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all" data-testid="close-sport-mental">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 pb-24 space-y-6">

          {/* ============ HUB ============ */}
          {!activeModule && (
            <>
              <div className="p-4 bg-white/5 rounded-2xl border border-orange-500/20" data-testid="sport-mental-intro-text">
                <p className="text-white/80 text-sm leading-relaxed">
                  La préparation mentale est utilisée par tous les grands champions. En visualisant votre performance,
                  en respirant efficacement et en ancrant des émotions positives, votre cerveau crée les mêmes connexions
                  que lors d'un entraînement réel : confiance renforcée, stress apaisé, gestes plus fluides le jour J.
                  Choisissez un module ci-dessous, installez-vous au calme et laissez-vous guider.
                </p>
              </div>

              <div className="space-y-2" data-testid="sport-modules-list">
                {visibleModules.map(m => (
                  <button key={m.id} onClick={() => setActiveModule(m.id)}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-orange-400/30 transition-all text-left group"
                    data-testid={`module-${m.id}`}>
                    <m.Icon className={`w-6 h-6 ${m.color} flex-shrink-0`} />
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium flex items-center gap-2">
                        {m.name}
                        {isAdmin && !isModuleVisible(m.id) && (
                          <span className="flex items-center gap-1 text-[10px] text-red-300/80 bg-red-500/10 px-1.5 py-0.5 rounded-full"><EyeOff className="w-3 h-3" /> masqué</span>
                        )}
                      </p>
                      <p className="text-white/50 text-xs">{m.desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-orange-300 transition-colors" />
                  </button>
                ))}
              </div>

              {/* Séances du coach */}
              {(seances.length > 0 || isAdmin) && (
                <div className="p-4 bg-white/5 rounded-2xl space-y-3" data-testid="coach-seances-section">
                  <p className="text-white/60 text-xs uppercase tracking-wider">Séances guidées du coach</p>
                  {seances.length === 0 && <p className="text-white/40 text-xs">Aucune séance enregistrée pour le moment.</p>}
                  {seances.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                      <span className="text-white/80 text-sm">{a.name}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleCoachAudio(a)} className="p-2 rounded-full bg-orange-500/20 text-orange-300 hover:bg-orange-500/30" data-testid={`play-seance-${a.id}`}>
                          {playingAudioId === a.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        {isAdmin && (
                          <button onClick={() => deleteAudio(a.id)} className="p-2 rounded-full bg-red-500/10 text-red-400/70 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {isAdmin && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button onClick={() => pickFile('seance')} disabled={uploading}
                        className="px-3 py-2 rounded-xl bg-white/10 text-white/70 text-xs flex items-center gap-1.5 hover:bg-white/15 disabled:opacity-50" data-testid="upload-seance-btn">
                        <Upload className="w-3.5 h-3.5" /> {uploading ? 'Envoi...' : 'Ajouter un MP3'}
                      </button>
                      {!isRecording ? (
                        <button onClick={startRecording}
                          className="px-3 py-2 rounded-xl bg-red-500/15 text-red-300 text-xs flex items-center gap-1.5 hover:bg-red-500/25" data-testid="record-seance-btn">
                          <Mic className="w-3.5 h-3.5" /> Enregistrer au micro
                        </button>
                      ) : (
                        <button onClick={stopRecording}
                          className="px-3 py-2 rounded-xl bg-red-500/40 text-white text-xs flex items-center gap-1.5 animate-pulse" data-testid="stop-record-btn">
                          <Square className="w-3.5 h-3.5" /> Arrêter l'enregistrement
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Suggestions supplémentaires */}
              {(suggestions.length > 0 || isAdmin) && (
                <div className="p-4 bg-white/5 rounded-2xl space-y-3" data-testid="suggestions-section">
                  <p className="text-white/60 text-xs uppercase tracking-wider">Suggestions supplémentaires</p>
                  {suggestions.length === 0 && <p className="text-white/40 text-xs">Aucune suggestion pour le moment.</p>}
                  {suggestions.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                      <span className="text-white/80 text-sm">{a.name}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleCoachAudio(a)} className="p-2 rounded-full bg-amber-500/20 text-amber-300 hover:bg-amber-500/30" data-testid={`play-suggestion-${a.id}`}>
                          {playingAudioId === a.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        {isAdmin && (
                          <button onClick={() => deleteAudio(a.id)} className="p-2 rounded-full bg-red-500/10 text-red-400/70 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {isAdmin && (
                    <button onClick={() => pickFile('suggestion')} disabled={uploading}
                      className="px-3 py-2 rounded-xl bg-white/10 text-white/70 text-xs flex items-center gap-1.5 hover:bg-white/15 disabled:opacity-50" data-testid="upload-suggestion-btn">
                      <Upload className="w-3.5 h-3.5" /> {uploading ? 'Envoi...' : 'Ajouter un MP3'}
                    </button>
                  )}
                </div>
              )}

              {/* Sons ambiants */}
              <div className="p-4 bg-white/5 rounded-2xl space-y-3" data-testid="sport-ambient-section">
                <p className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2"><Music className="w-3.5 h-3.5" /> Sons ambiants</p>
                {ambientSounds.length === 0 && <p className="text-white/40 text-xs">Aucun son ambiant disponible.</p>}
                <div className="space-y-2">
                  {ambientSounds.map(sound => (
                    <div key={sound.id} className={`p-2.5 rounded-xl ${activeAmbients[sound.id] ? 'bg-orange-500/15 border border-orange-400/30' : 'bg-white/5'}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-white/80 text-sm">{sound.name}</span>
                        <button onClick={() => toggleAmbient(sound)}
                          className={`px-3 py-1 rounded-full text-xs ${activeAmbients[sound.id] ? 'bg-orange-500/40 text-white' : 'bg-white/10 text-white/60'}`}
                          data-testid={`ambient-toggle-${sound.id}`}>
                          {activeAmbients[sound.id] ? 'Actif' : 'Activer'}
                        </button>
                      </div>
                      {activeAmbients[sound.id] && (
                        <div className="flex items-center gap-2 mt-2">
                          <Volume2 className="w-3.5 h-3.5 text-orange-300" />
                          <Slider value={[(ambientVolumes[sound.id] || 0.3) * 100]} onValueChange={([v]) => updateAmbientVolume(sound.id, v / 100)} max={100} className="flex-1" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-white/40 text-[11px]">Les sons ambiants continuent pendant vos séances dans tous les modules.</p>
              </div>

              {/* Admin : visibilité */}
              {isAdmin && (
                <div className="p-4 bg-amber-500/10 rounded-2xl border border-amber-500/30 space-y-4" data-testid="sport-mental-admin-section">
                  <p className="text-amber-300 text-xs uppercase tracking-wider">Administration</p>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!settings.sport_mental_visible}
                      onChange={(e) => update({ sport_mental_visible: e.target.checked })}
                      className="w-5 h-5 accent-amber-500"
                      data-testid="sport-mental-visibility-checkbox"
                    />
                    <span className="text-white/80 text-sm flex items-center gap-2">
                      {settings.sport_mental_visible ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-white/40" />}
                      Rendre ce module visible pour les utilisateurs
                    </span>
                  </label>

                  {/* Visibilité par sous-menu */}
                  <div className="pt-2 border-t border-amber-500/20">
                    <p className="text-white/60 text-xs mb-2">Sous-menus visibles par les utilisateurs :</p>
                    <div className="space-y-1.5">
                      {MODULES.map(m => (
                        <label key={m.id} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isModuleVisible(m.id)}
                            onChange={(e) => update({ sport_mental_modules_visible: { ...modVis, [m.id]: e.target.checked } })}
                            className="w-4 h-4 accent-amber-500"
                            data-testid={`module-visibility-${m.id}`}
                          />
                          <span className={`text-sm flex items-center gap-2 ${isModuleVisible(m.id) ? 'text-white/80' : 'text-white/40 line-through'}`}>
                            <m.Icon className={`w-3.5 h-3.5 ${m.color}`} /> {m.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Visibilité par voix */}
                  <div className="pt-2 border-t border-amber-500/20">
                    <p className="text-white/60 text-xs mb-2">Voix visibles par les utilisateurs :</p>
                    <div className="space-y-1.5">
                      {[
                        { id: 'female', label: 'Denise · Féminine' },
                        { id: 'male', label: 'Henri · Masculine' },
                        { id: 'coach', label: 'Coach · Ma voix (visible seulement si des enregistrements existent)' }
                      ].map(v => (
                        <label key={v.id} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isVoiceVisible(v.id)}
                            onChange={(e) => update({ sport_mental_voices_visible: { ...voiceVis, [v.id]: e.target.checked } })}
                            className="w-4 h-4 accent-amber-500"
                            data-testid={`voice-visibility-${v.id}`}
                          />
                          <span className={`text-sm ${isVoiceVisible(v.id) ? 'text-white/80' : 'text-white/40 line-through'}`}>{v.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Scripts Word */}
                  <div className="pt-2 border-t border-amber-500/20">
                    <a href="/scripts-preparation-mentale.docx" download
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500/15 border border-sky-400/30 text-sky-300 text-xs font-medium hover:bg-sky-500/25"
                      data-testid="download-all-scripts-word">
                      <Upload className="w-3.5 h-3.5 rotate-180" /> Télécharger tous les scripts (Word)
                    </a>
                    <p className="text-white/40 text-[11px] mt-1.5">Les scripts sont aussi lisibles à l'écran pendant l'enregistrement micro (bouton « Script » de chaque séance).</p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============ MODULE 1 : VISUALISATION ============ */}
          {activeModule === 'visualisation' && (
            <>
              <div className="p-4 bg-white/5 rounded-2xl border border-orange-500/20">
                <p className="text-white/80 text-sm leading-relaxed">
                  Visualisez votre performance avec vos cinq sens : votre cerveau crée les mêmes connexions que
                  lors d'un entraînement réel. Installez-vous au calme, fermez les yeux et laissez-vous guider.
                </p>
              </div>
              <CustomizeBlock sport={sport} setSport={setSport} sensation={sensation} setSensation={setSensation} emotion={emotion} setEmotion={setEmotion} />
              <VoiceSelector />
              <InductionBlock />
              {isAdmin && <CoachVoiceAdmin target="visualisation" label="Séance Visualisation" {...coachAdminProps} />}
              <PlayButton player={player} onStart={() => startGuidedSession(buildVisualisationScript(sport, sensation, emotion), 'visualisation')} />
            </>
          )}

          {/* ============ MODULE 2 : RESPIRATIONS SPORT ============ */}
          {activeModule === 'respiration' && (
            <>
              <div className="p-4 bg-white/5 rounded-2xl border border-cyan-500/20">
                <p className="text-white/80 text-sm leading-relaxed">
                  Trois respirations conçues pour le sport : apaiser le stress avant l'épreuve,
                  activer votre énergie avant l'effort, ou récupérer après l'entraînement.
                  Suivez le cercle et la voix.
                </p>
              </div>
              <VoiceSelector />
              {isAdmin && (
                <div className="space-y-2">
                  <p className="text-amber-300/70 text-[11px]">🎙️ Enregistrez les 3 consignes avec votre voix (utilisées quand la voix « Coach » est sélectionnée) :</p>
                  <CoachVoiceAdmin target="respiration_inhale" label="Consigne « Inspirez »" {...coachAdminProps} />
                  <CoachVoiceAdmin target="respiration_exhale" label="Consigne « Expirez »" {...coachAdminProps} />
                  <CoachVoiceAdmin target="respiration_hold" label="Consigne « Retenez »" {...coachAdminProps} />
                </div>
              )}
              <BreathingSportModule voiceGender={voiceGender} coachVoiceMap={coachVoiceMap} />
            </>
          )}

          {/* ============ MODULE 3 : AFFIRMATIONS DE CHAMPION ============ */}
          {activeModule === 'affirmations' && (
            <>
              <div className="p-4 bg-white/5 rounded-2xl border border-amber-500/20">
                <p className="text-white/80 text-sm leading-relaxed">
                  Répétez ces affirmations à voix haute ou mentalement, chaque jour et avant vos compétitions.
                  Votre cerveau finit par les intégrer comme des vérités : c'est la base du dialogue interne du champion.
                </p>
              </div>
              <VoiceSelector />
              {isAdmin && <CoachVoiceAdmin target="affirmations" label="Affirmations de Champion" {...coachAdminProps} />}
              <div className="flex justify-center">
                <PlayButton player={player}
                  onStart={() => {
                    if (voiceGender === 'coach') {
                      const main = coachVoiceMap['affirmations'];
                      if (!main) { alert('Aucun enregistrement de votre voix pour les affirmations. Utilisez le bouton 🎙️ Ma voix (mode admin).'); return; }
                      player.playUrls([main.audio_url], 'affirmations-all');
                    } else {
                      player.play(CHAMPION_AFFIRMATIONS.map(a => ({ ...a, pause: 4 })), voiceGender, 'affirmations-all');
                    }
                  }}
                  startLabel="Écouter toutes les affirmations" startTestId="play-all-affirmations" stopTestId="stop-affirmations" />
              </div>
              <div className="space-y-2" data-testid="affirmations-list">
                {CHAMPION_AFFIRMATIONS.map(a => (
                  <div key={a.id} className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-xl">
                    <p className="text-white/80 text-sm italic">"{a.text}"</p>
                    <button
                      onClick={() => player.playingKey === a.id ? player.stop() : player.play([{ ...a, pause: 0 }], voiceGender === 'coach' ? 'female' : voiceGender, a.id)}
                      className="p-2 rounded-full bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 flex-shrink-0"
                      data-testid={`play-affirmation-${a.id}`}>
                      {player.playingKey === a.id ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ============ MODULE 4 : ROUTINE D'AVANT-MATCH ============ */}
          {activeModule === 'routine' && (
            <>
              <div className="p-4 bg-white/5 rounded-2xl border border-emerald-500/20">
                <p className="text-white/80 text-sm leading-relaxed">
                  Votre rituel complet d'avant-compétition (environ 8 minutes) : respiration anti-stress,
                  visualisation de votre performance, affirmations de champion, puis activation finale.
                  À faire dans le vestiaire, dans les transports ou chez vous avant de partir.
                </p>
              </div>
              <CustomizeBlock sport={sport} setSport={setSport} sensation={sensation} setSensation={setSensation} emotion={emotion} setEmotion={setEmotion} />
              <VoiceSelector />
              <InductionBlock />
              {isAdmin && <CoachVoiceAdmin target="routine" label="Routine d'avant-match" {...coachAdminProps} />}
              <PlayButton player={player} onStart={() => startGuidedSession(buildRoutineScript(sport, sensation, emotion), 'routine')}
                startLabel="Lancer ma routine" startTestId="start-routine" stopTestId="stop-routine" />
            </>
          )}

          {/* ============ MODULE 5 : GESTION DES BLOCAGES ============ */}
          {activeModule === 'blocages' && (
            <>
              <div className="p-4 bg-white/5 rounded-2xl border border-purple-500/20">
                <p className="text-white/80 text-sm leading-relaxed">
                  Des séances ciblées pour dépasser les blocages les plus fréquents chez les sportifs.
                  Choisissez la vôtre, installez-vous au calme et laissez-vous accompagner.
                </p>
              </div>
              <VoiceSelector />
              <InductionBlock />
              <div className="space-y-3" data-testid="blocages-list">
                {BLOCAGE_SESSIONS.map(b => (
                  <div key={b.id} className={`p-4 rounded-2xl border transition-all ${
                    player.playingKey === `blocage_${b.id}` ? 'bg-purple-500/15 border-purple-400/40' : 'bg-white/5 border-white/10'
                  }`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-white text-sm font-medium">{b.name}</p>
                        <p className="text-white/50 text-xs">{b.desc}</p>
                      </div>
                      <button
                        onClick={() => player.playingKey === `blocage_${b.id}` ? player.stop() : startGuidedSession(b.script, `blocage_${b.id}`)}
                        disabled={player.isLoading}
                        className="px-4 py-2.5 rounded-full bg-purple-500/25 text-purple-200 text-xs font-medium hover:bg-purple-500/40 flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50"
                        data-testid={`play-blocage-${b.id}`}>
                        {player.playingKey === `blocage_${b.id}` ? <><Square className="w-3.5 h-3.5" /> Arrêter</> : <><Play className="w-3.5 h-3.5" /> Écouter</>}
                      </button>
                    </div>
                    {player.playingKey === `blocage_${b.id}` && (
                      <p className="text-purple-300/70 text-xs mt-2">Étape {player.progress.current}/{player.progress.total}</p>
                    )}
                    {isAdmin && (
                      <div className="mt-3">
                        <CoachVoiceAdmin target={`blocage_${b.id}`} label={b.name} {...coachAdminProps} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {scriptView && (
        <ScriptViewer
          title={scriptView.title}
          lines={scriptView.lines}
          recording={recordingTarget === scriptView.target}
          onStopRecord={stopRecording}
          onClose={() => setScriptView(null)}
        />
      )}
    </div>
  );
};

export default SportMentalPanel;
